/**
 * Ground Truth List View - ADMIN only
 * Displays a paginated, searchable list of all ground truth records
 * Matches the design of BondsView
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import GroundTruthService, { downloadExport } from './services/groundTruthService';
import {
  IMasterGroundTruth,
  GroundTruthFilters,
  GroundTruthValidationStatus,
  DocumentType,
  getStatusColor
} from './types/groundTruth';
import PageInfo from './components/PageInfo';
import './App.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

const GroundTruthListView: React.FC = () => {
  const { token, isAuthenticated, authLoaded, hasRole, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State management
  const [records, setRecords] = useState<IMasterGroundTruth[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<boolean>(false);
  const [showExportMenu, setShowExportMenu] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Filter state
  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [documentType] = useState<DocumentType | ''>(
    (searchParams.get('document_type') as DocumentType) || ''
  );
  const [validationStatus] = useState<GroundTruthValidationStatus | ''>(
    (searchParams.get('validation_status') as GroundTruthValidationStatus) || ''
  );
  const [sourceFilter, setSourceFilter] = useState<'all' | 'master' | 'ground-truth'>(
    (searchParams.get('source') as 'all' | 'master' | 'ground-truth') || 'ground-truth'
  );
  const [sourceNameFilter, setSourceNameFilter] = useState<string>(
    searchParams.get('source_name') || ''
  );
  const [availableSources, setAvailableSources] = useState<string[]>([]);
  const [sourcesLoading, setSourcesLoading] = useState<boolean>(false);

  // Tag filter state - default to empty (show all) if not specified in URL
  // Note: Database uses tags like "GREEN BOND", "Green", "Social", "Sustainability", "Sustainability-Linked"
  const [selectedTags, setSelectedTags] = useState<string[]>(() => {
    const tagsFromUrl = searchParams.get('tags');
    if (tagsFromUrl) {
      return tagsFromUrl.split(',').filter(t => t.trim());
    }
    return []; // Default to no tag filter (show all)
  });
  const [sortBy] = useState<'created_at' | 'updated_at' | 'document_id' | 'source_name'>(
    (searchParams.get('sortBy') as any) || 'updated_at'
  );
  const [sortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc'
  );

  // Redirect non-ADMIN users
  useEffect(() => {
    if (authLoaded && (!isAuthenticated || !hasRole('ADMIN'))) {
      navigate('/', { replace: true });
    }
  }, [authLoaded, isAuthenticated, hasRole, navigate]);

  // Fetch ground truth records
  const fetchRecords = useCallback(async (filters: GroundTruthFilters) => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await GroundTruthService.getList(filters);

      setRecords(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      console.error('Error fetching ground truth records:', err);
      setError(err.message || 'Failed to fetch ground truth records');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Load records on mount and when filters change
  useEffect(() => {
    if (!authLoaded || !token) return;

    const page = parseInt(searchParams.get('page') || '1', 10);

    const filters: GroundTruthFilters = {
      page,
      limit: pagination.limit,
      search: searchQuery || undefined,
      document_type: documentType || undefined,
      validation_status: validationStatus || undefined,
      tags: selectedTags.length > 0 ? selectedTags : undefined,
      source: sourceFilter !== 'all' ? sourceFilter : undefined,
      source_name: sourceNameFilter || undefined, // Only applied when not master
      sortBy,
      sortOrder
    };

    fetchRecords(filters);
  }, [
    authLoaded,
    token,
    searchParams,
    searchQuery,
    documentType,
    validationStatus,
    selectedTags,
    sourceFilter,
    sourceNameFilter,
    sortBy,
    sortOrder,
    pagination.limit,
    fetchRecords
  ]);

  // Fetch available sources when not showing master
  useEffect(() => {
    console.log('Source fetch useEffect triggered:', {
      authLoaded,
      hasToken: !!token,
      sourceFilter,
      willFetch: authLoaded && token && sourceFilter !== 'master'
    });

    if (!authLoaded || !token || sourceFilter === 'master') {
      setAvailableSources([]);
      setSourcesLoading(false);
      return;
    }

    const fetchSources = async () => {
      try {
        setSourcesLoading(true);
        console.log('Fetching unique sources from API...');
        const sources = await GroundTruthService.getUniqueSources();
        console.log('API Response - sources:', sources);
        console.log('API Response - sources count:', sources ? sources.length : 0);
        setAvailableSources(sources || []);
      } catch (err: any) {
        console.error('Error fetching sources:', err);
        console.error('Error details:', err.response || err.message);
        setAvailableSources([]);
      } finally {
        setSourcesLoading(false);
      }
    };

    fetchSources();
  }, [authLoaded, token, sourceFilter]);

  // Update URL params when filters change
  const updateUrlParams = useCallback((params: Record<string, string>) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });

    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Handle search
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateUrlParams({ search: searchQuery, page: '1' });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  // Handle filter changes
  const handleSourceFilterChange = (value: 'all' | 'master' | 'ground-truth') => {
    setSourceFilter(value);
    // Clear source_name filter when switching to master (master has many sources)
    if (value === 'master') {
      setSourceNameFilter('');
      updateUrlParams({ source: value, source_name: '', page: '1' });
    } else {
      updateUrlParams({ source: value, page: '1' });
    }
  };

  const handleSourceNameChange = (value: string) => {
    setSourceNameFilter(value);
    updateUrlParams({ source_name: value, page: '1' });
  };

  // Handle pagination
  const handlePageChange = useCallback((newPage: number) => {
    updateUrlParams({ page: newPage.toString() });
  }, [updateUrlParams]);

  // Handle row click - navigate to detail view
  const handleRowClick = useCallback((record: IMasterGroundTruth) => {
    navigate(`/ground-truth/${record._id}`);
  }, [navigate]);

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    updateUrlParams({ search: '', page: '1' });
  };

  // Handle export
  const handleExport = async (format: 'csv' | 'json') => {
    try {
      setExporting(true);
      setShowExportMenu(false);
      setError(null);

      const filters: GroundTruthFilters = {
        search: searchQuery || undefined,
        document_type: documentType || undefined,
        validation_status: validationStatus || undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        source: sourceFilter !== 'all' ? sourceFilter : undefined,
        source_name: sourceNameFilter || undefined,
      };

      const blob = await GroundTruthService.export(format, filters);
      const filename = `ground-truth-export-${new Date().toISOString().split('T')[0]}.${format}`;
      downloadExport(blob, filename);
    } catch (err: any) {
      console.error('Error exporting ground truth:', err);
      setError(err.message || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showExportMenu]);

  const formatDate = (dateValue: string | Date | undefined): string => {
    if (!dateValue) {
      return 'N/A';
    }

    if (dateValue instanceof Date) {
      return dateValue.toLocaleDateString();
    }

    if (typeof dateValue === 'string' && dateValue.trim() === '') {
      return 'N/A';
    }

    try {
      const date = new Date(dateValue as string);
      if (!isNaN(date.getTime())) {
        return new Intl.DateTimeFormat(navigator.language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).format(date);
      }
    } catch {
      console.warn(`Unable to parse date: "${dateValue}"`);
    }

    return String(dateValue);
  };

  if (!authLoaded || loading) {
    return (
      <div className="loading-container">
        <div className="bonds-loader-flex">
          <i className="fas fa-spinner fa-spin loader-icon" aria-label="Loading" />
          <span className="loader-text">{t('loading.groundTruth', 'Loading ground truth records...')}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">
          <i className="fas fa-exclamation-triangle" style={{ marginRight: '10px', color: '#e74c3c' }}></i>
          {error}
        </div>
        <button onClick={() => navigate('/ground-truth')} className="btn-primary" style={{ marginTop: '20px' }}>
          <i className="fas fa-arrow-left" style={{ marginRight: '8px' }}></i>
          {t('buttons.back', 'Back')}
        </button>
      </div>
    );
  }

  const hasActiveFilters = searchQuery || documentType || validationStatus || sourceFilter !== 'ground-truth' || selectedTags.length > 0;

  return (
    <div className="table-container">
      {/* Header Section */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
          {t('groundTruth.title', 'Ground Truth')}
        </h1>
        <div className="export-menu-container" style={{ position: 'relative' }}>
          <button
            className="bonds-export-button"
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={exporting}
            style={{
              color: '#ffffff',
              backgroundColor: '#10b981'
            }}
          >
            {exporting ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: '5px' }}></i>
                {t('buttons.exporting', 'Exporting...')}
              </>
            ) : (
              <>
                <i className="fas fa-download" style={{ marginRight: '5px' }}></i>
                {t('buttons.exportData', 'Export Data')}
                <i className="fas fa-caret-down" style={{ marginLeft: '5px', fontSize: '12px' }}></i>
              </>
            )}
          </button>
          {showExportMenu && !exporting && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              minWidth: '150px'
            }}>
              <button
                onClick={() => handleExport('csv')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  textAlign: 'left',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <i className="fas fa-file-csv" style={{ color: '#10b981' }}></i>
                {t('buttons.exportCSV', 'Export as CSV')}
              </button>
              <button
                onClick={() => handleExport('json')}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  textAlign: 'left',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  borderTop: '1px solid #e5e7eb'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <i className="fas fa-file-code" style={{ color: '#3b82f6' }}></i>
                {t('buttons.exportJSON', 'Export as JSON')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Search Section */}
      <div className="bonds-search-section">
        <form onSubmit={handleSearchSubmit} className="bonds-search-form">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('groundTruth.searchPlaceholder', 'Search ground truth by document ID, source name, or title...')}
            className="bonds-search-input"
          />
          <button
            type="submit"
            disabled={!searchQuery.trim()}
            className="bonds-search-button"
          >
            <i className="fas fa-search" style={{ marginRight: '5px' }}></i>
            {t('buttons.search', 'Search')}
          </button>
          {searchQuery && (
            <button
              type="button"
              onClick={clearSearch}
              className="bonds-clear-button"
            >
              <i className="fas fa-times" style={{ marginRight: '5px' }}></i>
              {t('buttons.clear', 'Clear')}
            </button>
          )}
        </form>
      </div>

      {/* Quick Filters Section */}
      <div className="bonds-quick-filters-section" style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong>{t('groundTruth.quickFilters', 'Quick Filters')}:</strong>
          <button
            onClick={() => handleSourceFilterChange('ground-truth')}
            className={sourceFilter === 'ground-truth' ? 'bonds-apply-tags-button' : 'bonds-clear-tags-button'}
            style={{ padding: '8px 16px' }}
          >
            {t('groundTruth.showAll', 'Show All Ground Truth')}
          </button>
          <button
            onClick={() => handleSourceFilterChange('master')}
            className={sourceFilter === 'master' ? 'bonds-apply-tags-button' : 'bonds-clear-tags-button'}
            style={{ padding: '8px 16px' }}
          >
            {t('groundTruth.showMaster', 'Show Master Only')}
          </button>
        </div>
      </div>

      {/* Tag Filters Section */}
      <div className="bonds-quick-filters-section" style={{ marginBottom: '15px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong>{t('groundTruth.tagFilters', 'Tag Filters')}:</strong>
          <button
            onClick={() => {
              if (selectedTags.includes('Green')) {
                setSelectedTags(selectedTags.filter(t => t !== 'Green'));
                updateUrlParams({ tags: selectedTags.filter(t => t !== 'Green').join(',') || '', page: '1' });
              } else {
                const newTags = [...selectedTags, 'Green'];
                setSelectedTags(newTags);
                updateUrlParams({ tags: newTags.join(','), page: '1' });
              }
            }}
            className={selectedTags.includes('Green') ? 'bonds-apply-tags-button' : 'bonds-clear-tags-button'}
            style={{ padding: '8px 16px', backgroundColor: selectedTags.includes('Green') ? '#10b981' : undefined }}
          >
            <i className="fas fa-leaf" style={{ marginRight: '5px' }}></i>
            {t('groundTruth.greenTag', 'Green')}
          </button>
          <button
            onClick={() => {
              if (selectedTags.includes('Social')) {
                setSelectedTags(selectedTags.filter(t => t !== 'Social'));
                updateUrlParams({ tags: selectedTags.filter(t => t !== 'Social').join(',') || '', page: '1' });
              } else {
                const newTags = [...selectedTags, 'Social'];
                setSelectedTags(newTags);
                updateUrlParams({ tags: newTags.join(','), page: '1' });
              }
            }}
            className={selectedTags.includes('Social') ? 'bonds-apply-tags-button' : 'bonds-clear-tags-button'}
            style={{ padding: '8px 16px', backgroundColor: selectedTags.includes('Social') ? '#3b82f6' : undefined }}
          >
            <i className="fas fa-users" style={{ marginRight: '5px' }}></i>
            {t('groundTruth.socialTag', 'Social')}
          </button>
          <button
            onClick={() => {
              if (selectedTags.includes('Sustainability')) {
                setSelectedTags(selectedTags.filter(t => t !== 'Sustainability'));
                updateUrlParams({ tags: selectedTags.filter(t => t !== 'Sustainability').join(',') || '', page: '1' });
              } else {
                const newTags = [...selectedTags, 'Sustainability'];
                setSelectedTags(newTags);
                updateUrlParams({ tags: newTags.join(','), page: '1' });
              }
            }}
            className={selectedTags.includes('Sustainability') ? 'bonds-apply-tags-button' : 'bonds-clear-tags-button'}
            style={{ padding: '8px 16px', backgroundColor: selectedTags.includes('Sustainability') ? '#8b5cf6' : undefined }}
          >
            <i className="fas fa-globe" style={{ marginRight: '5px' }}></i>
            {t('groundTruth.sustainabilityTag', 'Sustainability')}
          </button>
          {selectedTags.length > 0 && (
            <button
              onClick={() => {
                setSelectedTags([]);
                updateUrlParams({ tags: '', page: '1' });
              }}
              className="bonds-clear-button"
              style={{ padding: '8px 16px' }}
            >
              <i className="fas fa-times" style={{ marginRight: '5px' }}></i>
              {t('groundTruth.clearTags', 'Clear Tags')}
            </button>
          )}
        </div>
      </div>

      {/* Source Name Filter - Only show when not filtering by master */}
      {sourceFilter !== 'master' && (
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
            <strong>{t('groundTruth.filterBySource', 'Filter by Source')}:</strong>
            <select
              value={sourceNameFilter}
              onChange={(e) => handleSourceNameChange(e.target.value)}
              disabled={sourcesLoading}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: sourcesLoading ? '#f3f4f6' : '#ffffff',
                color: '#374151',
                minWidth: '200px',
                cursor: sourcesLoading ? 'not-allowed' : 'pointer'
              }}
            >
              <option value="">
                {sourcesLoading
                  ? t('groundTruth.loadingSources', 'Loading sources...')
                  : t('groundTruth.allSources', 'All Sources')}
              </option>
              {availableSources.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {/* Pagination Information */}
      {pagination.total > 0 && (
        <div className="pagination-container">
          <PageInfo
            count={records.length}
            total={pagination.total}
            label={t('groundTruth.label', 'Ground Truth Records')}
            showPageInfo={false}
            extra={isAuthenticated && user ? `(${t('user.loggedInAs', 'Logged in as')} ${user.role})` : undefined}
          />
          {/* Pagination Controls */}
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              <i className="fas fa-angle-double-left"></i>
            </button>

            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              <i className="fas fa-angle-left"></i>
            </button>

            <span className="pagination-page-info">Page {pagination.page} of {pagination.totalPages}</span>

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="pagination-button"
            >
              <i className="fas fa-angle-right"></i>
            </button>

            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.page === pagination.totalPages}
              className="pagination-button"
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {records.length > 0 ? (
        <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  {t('groundTruth.documentId', 'DOCUMENT ID')}
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  {t('groundTruth.source', 'SOURCE')}
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  {t('groundTruth.title', 'TITLE')}
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  {t('groundTruth.type', 'TYPE')}
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  {t('groundTruth.status', 'STATUS')}
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  {t('groundTruth.updated', 'UPDATED')}
                </th>
              </tr>
            </thead>
            <tbody>
              {records.map((record, index) => (
                <tr
                  key={record._id}
                  onClick={() => handleRowClick(record)}
                  style={{
                    cursor: 'pointer',
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb';
                  }}
                >
                  <td style={{ padding: '12px 16px', color: '#10b981', fontWeight: '500', fontSize: '14px' }}>
                    {record.document_id || 'N/A'}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#374151', fontSize: '14px' }}>
                    {record.source_name || 'N/A'}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#374151', fontSize: '14px' }}>
                    {record.title || 'N/A'}
                  </td>
                  <td style={{ padding: '12px 16px', color: '#374151', fontSize: '14px' }}>
                    {record.document_type || 'N/A'}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: getStatusColor(record.validation_status) + '20',
                        color: getStatusColor(record.validation_status)
                      }}
                    >
                      {record.validation_status || 'Pending'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', color: '#374151', fontSize: '14px' }}>
                    {formatDate(record.updated_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-folder-open" style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '16px' }}></i>
          <h3 style={{ color: '#374151', marginBottom: '8px' }}>
            {t('groundTruth.noRecordsFound', 'No ground truth records found')}
          </h3>
          <p style={{ color: '#6b7280' }}>
            {hasActiveFilters
              ? t('groundTruth.tryAdjustingFilters', 'Try adjusting your filters or search criteria.')
              : t('groundTruth.noRecordsCreated', 'No ground truth records have been created yet.')}
          </p>
        </div>
      )}

      {/* Bottom Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination-container" style={{ marginTop: '20px' }}>
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              <i className="fas fa-angle-double-left"></i>
            </button>

            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="pagination-button"
            >
              <i className="fas fa-angle-left"></i>
            </button>

            <span className="pagination-page-info">Page {pagination.page} of {pagination.totalPages}</span>

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="pagination-button"
            >
              <i className="fas fa-angle-right"></i>
            </button>

            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.page === pagination.totalPages}
              className="pagination-button"
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroundTruthListView;
